import { getPropertyImagesByInternalService } from '~/services/AccommodationService';

import ApiError from '~/types/ApiError';

export const ACCOMMODATION_INTERNAL_SERVICE = 'reservation';
// Fetch more than the sync cap so images already on the offer are filtered out
// without crowding out genuinely new ones.
export const SYNC_PROPERTY_FETCH_LIMIT = 75;
export const SYNC_TOP_ROOM_IMAGE_COUNT = 10;
export const SYNC_ROOM_FETCH_LIMIT = 40;

export interface AccomPropertyImage {
  id: string;
  publicId: string;
  title?: string;
  canonicalImageId: string | null;
  alreadyOnOffer: boolean;
  isDuplicate: boolean;
}

export function getCanonicalRootId(
  image: { id: string; canonicalImageId: string | null },
  imagesById: Map<string, { id: string; canonicalImageId: string | null }>,
): string {
  let rootId = image.id;
  const seenInChain = new Set<string>();

  while (imagesById.get(rootId)?.canonicalImageId) {
    if (seenInChain.has(rootId)) {
      break;
    }
    seenInChain.add(rootId);
    rootId = imagesById.get(rootId)?.canonicalImageId;
  }

  return rootId;
}

// Root choice in a canonicalImageId chain is arbitrary, so any imported member marks the whole group as duplicate.
export function resolveCanonicalRootIds<T extends { id: string; canonicalImageId: string | null }>(
  images: Array<T>,
  isAlreadyImported: (image: T) => boolean = () => false,
): Set<string> {
  const imagesById = new Map(images.map((image) => [image.id, image]));
  const alreadyImportedRootIds = new Set<string>();

  images.forEach((image) => {
    if (isAlreadyImported(image)) {
      alreadyImportedRootIds.add(getCanonicalRootId(image, imagesById));
    }
  });

  const duplicateIds = new Set<string>();

  images.forEach((image) => {
    const rootId = getCanonicalRootId(image, imagesById);

    if (alreadyImportedRootIds.has(rootId) || rootId !== image.id) {
      duplicateIds.add(image.id);
    }
  });

  return duplicateIds;
}

export async function fetchAccomPropertyImages(
  propertyId: string,
  existingPublicIds: Set<string>,
): Promise<Array<AccomPropertyImage>> {
  const response = await getPropertyImagesByInternalService({
    internalService: ACCOMMODATION_INTERNAL_SERVICE,
    internalServiceId: propertyId,
    limit: SYNC_PROPERTY_FETCH_LIMIT,
  });
  const images = response.result?.images ?? [];
  const duplicateImageIds = resolveCanonicalRootIds(images, (image) => existingPublicIds.has(image.imageId));

  return images.map((image) => ({
    id: image.id,
    publicId: image.imageId,
    title: image.title ?? undefined,
    canonicalImageId: image.canonicalImageId ?? null,
    alreadyOnOffer: existingPublicIds.has(image.imageId),
    isDuplicate: duplicateImageIds.has(image.id),
  }));
}

export function isPropertyNotMappedError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}
